<template>
  <div class="card shadow-sm m-b-35 pay-ops-usage">
    <div
      class="card-header pay-ops-usage__header"
      role="button"
      tabindex="0"
      :aria-expanded="expanded"
      aria-controls="pay-ops-usage-body"
      @click="toggle"
      @keydown.enter="toggle"
      @keydown.space.prevent="toggle"
    >
      <div class="d-flex align-items-center gap-2">
        <span class="pay-ops-usage__header-icon">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
        </span>
        <h6 class="mb-0 fw-semibold">Usage Instructions</h6>
        <span class="badge ms-auto pay-ops-usage__access" :class="headerAccessClass">
          <i
            class="fa me-1"
            :class="hasEditAccess ? 'fa-check-circle' : hasViewAccess ? 'fa-eye' : 'fa-lock'"
            aria-hidden="true"
          ></i>
          {{ headerAccessLabel }}
        </span>
        <span class="pay-ops-usage__toggle" aria-hidden="true">
          <i class="fa" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </span>
      </div>
    </div>

    <div v-show="expanded" id="pay-ops-usage-body" class="card-body p-4">
      <div v-if="description" class="pay-ops-usage__desc mb-4">
        <i class="fa fa-lightbulb-o" aria-hidden="true"></i>
        <span>{{ description }}</span>
      </div>

      <section class="pay-ops-usage__section">
        <div class="pay-ops-usage__section-heading">
          <div>
            <div class="pay-ops-usage__section-label">
              <i class="fa fa-shield me-1" aria-hidden="true"></i>
              Access And Roles
            </div>
            <p class="small text-muted mb-0">
              Each access level lists alternative roles. One matching role is sufficient.
            </p>
          </div>
          <div
            class="pay-ops-usage__role-summary"
            :class="
              hasViewAccess
                ? 'pay-ops-usage__role-summary--ok'
                : 'pay-ops-usage__role-summary--missing'
            "
          >
            <i
              class="fa"
              :class="hasViewAccess ? 'fa-check-circle' : 'fa-lock'"
              aria-hidden="true"
            ></i>
            <span>
              <strong>{{
                hasEditAccess ? 'Management Access' : hasViewAccess ? 'View Access' : 'No Access'
              }}</strong>
              {{ matchedRole ? `via ${matchedRole}` : 'required role not assigned' }}
            </span>
          </div>
        </div>

        <div class="pay-ops-usage__access-levels">
          <article
            class="pay-ops-usage__access-level"
            :class="{ 'pay-ops-usage__access-level--available': hasViewAccess }"
          >
            <span class="pay-ops-usage__level-icon">
              <i class="fa" :class="hasViewAccess ? 'fa-check' : 'fa-lock'" aria-hidden="true"></i>
            </span>
            <div class="pay-ops-usage__level-content">
              <div class="pay-ops-usage__level-heading">
                <strong>View This Page</strong>
                <span>{{ hasViewAccess ? 'Available' : 'Access Required' }}</span>
              </div>
              <p>
                {{
                  hasViewAccess ? 'Required Role Satisfied By:' : 'One Of These Roles Is Required:'
                }}
              </p>
              <div class="pay-ops-usage__role-options">
                <code
                  v-for="roleId in displayedViewRoleIds"
                  :key="roleId"
                  :class="{ 'pay-ops-usage__role-id--matched': hasRole(roleId) }"
                >
                  <i v-if="hasRole(roleId)" class="fa fa-check me-1" aria-hidden="true"></i
                  >{{ roleId }}
                </code>
              </div>
            </div>
          </article>

          <article
            class="pay-ops-usage__access-level"
            :class="{ 'pay-ops-usage__access-level--available': hasEditAccess }"
          >
            <span class="pay-ops-usage__level-icon">
              <i class="fa" :class="hasEditAccess ? 'fa-check' : 'fa-lock'" aria-hidden="true"></i>
            </span>
            <div class="pay-ops-usage__level-content">
              <div class="pay-ops-usage__level-heading">
                <strong>Edit This Page</strong>
                <span>{{ hasEditAccess ? 'Available' : 'Access Required' }}</span>
              </div>
              <p>
                {{
                  hasEditAccess ? 'Required Role Satisfied By:' : 'One Of These Roles Is Required:'
                }}
              </p>
              <div class="pay-ops-usage__role-options">
                <code
                  v-for="roleId in displayedEditRoleIds"
                  :key="roleId"
                  :class="{ 'pay-ops-usage__role-id--matched': hasRole(roleId) }"
                >
                  <i v-if="hasRole(roleId)" class="fa fa-check me-1" aria-hidden="true"></i
                  >{{ roleId }}
                </code>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="workflowNotes.length" class="pay-ops-usage__section">
        <div class="pay-ops-usage__section-heading">
          <div>
            <div class="pay-ops-usage__section-label">
              <i class="fa fa-compass me-1" aria-hidden="true"></i>
              Workflow Guidance
            </div>
            <p class="small text-muted mb-0">
              Use these notes to identify the right operational path and expected action.
            </p>
          </div>
        </div>
        <ul class="pay-ops-usage__note-list">
          <li
            v-for="(note, idx) in workflowNotes"
            :key="idx"
            class="pay-ops-usage__note-row"
            :class="noteRowAccentClass(note)"
          >
            <span class="pay-ops-usage__note-icon" :class="noteAccentClass(note)">
              <i class="fa" :class="note.icon || 'fa-info'" aria-hidden="true"></i>
            </span>
            <div class="pay-ops-usage__note-content">
              <strong v-if="note.title" class="pay-ops-usage__note-title">{{ note.title }}</strong>
              <span class="pay-ops-usage__note-body" v-html="note.body"></span>
            </div>
          </li>
        </ul>
      </section>

      <slot name="extra" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Roles } from '@/constants/roles';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';

const props = defineProps({
  description: { type: String, default: '' },
  requiredRoles: {
    type: Array,
    default: () => [],
  },
  workflowNotes: {
    type: Array,
    default: () => [],
  },
});

const { roles } = useAmendmentRoles();
const expanded = ref(false);

const userRoleSet = computed(() => new Set(roles.value));
const requiredRoleIds = computed(() => props.requiredRoles.map((role) => role.id));

const viewRoleIds = computed(() => requiredRoleIds.value);
const editRoleIds = computed(() => {
  const editRoles = requiredRoleIds.value.filter((roleId) =>
    [Roles.pay_operations_agent, Roles.pay_operations_admin].includes(roleId),
  );
  return editRoles.length ? editRoles : requiredRoleIds.value;
});

function hasRole(roleId) {
  return userRoleSet.value.has(roleId);
}

function toggle() {
  expanded.value = !expanded.value;
}

const hasViewAccess = computed(() => viewRoleIds.value.some(hasRole));
const hasEditAccess = computed(() => editRoleIds.value.some(hasRole));
const matchedRole = computed(() =>
  [Roles.pay_operations_admin, Roles.pay_operations_agent, Roles.pay_operations_viewer].find(
    hasRole,
  ),
);
const displayedViewRoleIds = computed(() =>
  hasViewAccess.value && matchedRole.value ? [matchedRole.value] : viewRoleIds.value,
);
const displayedEditRoleIds = computed(() =>
  hasEditAccess.value && matchedRole.value ? [matchedRole.value] : editRoleIds.value,
);
const headerAccessLabel = computed(() =>
  hasEditAccess.value
    ? 'Management Access'
    : hasViewAccess.value
      ? 'View Access'
      : 'Access Required',
);
const headerAccessClass = computed(() =>
  hasViewAccess.value ? 'pay-ops-usage__access--ok' : 'pay-ops-usage__access--missing',
);

function noteAccentClass(note) {
  const cls = (note.badgeClass || '').toLowerCase();
  if (cls.includes('bg-success')) return 'pay-ops-usage__note-icon--success';
  if (cls.includes('bg-warning')) return 'pay-ops-usage__note-icon--warning';
  if (cls.includes('bg-danger')) return 'pay-ops-usage__note-icon--danger';
  if (cls.includes('bg-info')) return 'pay-ops-usage__note-icon--info';
  if (cls.includes('bg-primary')) return 'pay-ops-usage__note-icon--primary';
  if (cls.includes('bg-dark')) return 'pay-ops-usage__note-icon--dark';
  return 'pay-ops-usage__note-icon--neutral';
}

function noteRowAccentClass(note) {
  return noteAccentClass(note).replace('note-icon', 'note-row');
}
</script>

<style scoped>
.pay-ops-usage {
  border: 1px solid #cfd6e4;
  border-radius: 0.9rem;
  overflow: hidden;
  background: #fff;
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.02),
    0 10px 26px rgba(15, 23, 42, 0.05) !important;
}

/* Header --------------------------------------------------------------- */
.pay-ops-usage__header {
  color: #202939;
  background: linear-gradient(135deg, #f5f8ff, #edf3ff);
  border-bottom: 1px solid #d6e2ff;
  padding: 1rem 1.25rem;
  cursor: pointer;
}

.pay-ops-usage__header:focus-visible {
  outline: 2px solid var(--theme-default);
  outline-offset: -3px;
}

.pay-ops-usage__header h6 {
  color: #202939;
  font-weight: 800 !important;
}

.pay-ops-usage__header-icon {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: #dbe7ff;
  color: #175cd3;
}

.pay-ops-usage__access {
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.35rem 0.6rem;
}

.pay-ops-usage__access--ok {
  border: 1px solid #a6e4c5;
  background: #d1fadf;
  color: #05603a;
}

.pay-ops-usage__access--missing {
  border: 1px solid #fecdca;
  background: #fee4e2;
  color: #b42318;
}

.pay-ops-usage__toggle {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #b6ccfe;
  border-radius: 50%;
  background: #fff;
  color: #2e3482;
}

.pay-ops-usage__toggle:hover,
.pay-ops-usage__toggle:focus {
  border-color: var(--theme-default);
  color: var(--theme-default);
}

.pay-ops-usage__header:focus-visible {
  outline: 2px solid var(--theme-default);
  outline-offset: -2px;
}

/* Description ---------------------------------------------------------- */
.pay-ops-usage__desc {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 72rem;
  border: 1px solid #b6ccfe;
  border-left: 4px solid #175cd3;
  border-radius: 0.55rem;
  background: #eff4ff;
  color: #344054;
  padding: 0.9rem 1rem;
  font-size: 0.93rem;
  line-height: 1.6;
  text-transform: none;
}

.pay-ops-usage__desc i {
  margin-top: 0.2rem;
  color: #175cd3;
  font-size: 1rem;
}

/* Section --------------------------------------------------------------- */
.pay-ops-usage__section + .pay-ops-usage__section {
  padding-top: 1.75rem;
  margin-top: 1.75rem;
  border-top: 2px solid #eaecf0;
}

.pay-ops-usage__section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.pay-ops-usage__section-heading p {
  color: #667085 !important;
  line-height: 1.5;
  text-transform: none;
}

.pay-ops-usage__section-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.4rem;
  padding: 0.3rem 0.55rem;
  border-radius: 0.35rem;
  color: #fff;
  background: #2e3482;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
}

.pay-ops-usage__role-summary {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 0 0 auto;
  border: 1px solid;
  border-radius: 0.6rem;
  padding: 0.55rem 0.75rem;
  font-size: 0.76rem;
  line-height: 1.25;
}

.pay-ops-usage__role-summary i {
  font-size: 1rem;
}

.pay-ops-usage__role-summary strong,
.pay-ops-usage__role-summary span {
  display: block;
}

.pay-ops-usage__role-summary--ok {
  border-color: rgba(25, 135, 84, 0.24);
  background: rgba(25, 135, 84, 0.055);
  color: #146c43;
}

.pay-ops-usage__role-summary--missing {
  border-color: rgba(220, 53, 69, 0.22);
  background: rgba(220, 53, 69, 0.045);
  color: #b02a37;
}

/* Access levels --------------------------------------------------------- */
.pay-ops-usage__access-levels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.pay-ops-usage__access-level {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  min-height: 7rem;
  padding: 0.9rem;
  border: 1px solid #d0d5dd;
  border-left: 4px solid #98a2b3;
  border-radius: 0.6rem;
  background: #f9fafb;
}

.pay-ops-usage__access-level--available {
  border-color: #6ce9a6;
  border-left: 4px solid #039855;
  background: #ecfdf3;
}

.pay-ops-usage__level-icon {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.7rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
  background: #eaecf0;
  color: #475467;
}

.pay-ops-usage__access-level--available .pay-ops-usage__level-icon {
  background: #039855;
  color: #fff;
}

.pay-ops-usage__level-content {
  flex: 1 1 auto;
  min-width: 0;
}

.pay-ops-usage__level-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pay-ops-usage__level-heading strong {
  color: #182230;
  font-size: 0.9rem;
}

.pay-ops-usage__level-heading span {
  color: #667085;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.pay-ops-usage__access-level--available .pay-ops-usage__level-heading span {
  color: #027a48;
}

.pay-ops-usage__level-content p {
  margin: 0.65rem 0 0.4rem;
  color: #667085;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.pay-ops-usage__role-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.pay-ops-usage__role-options code {
  border: 1px solid #c7d7fe;
  background: #fff;
  color: #2e3482;
  padding: 0.25rem 0.48rem;
  border-radius: 0.3rem;
  font-size: 0.74rem;
  font-weight: 700;
}

.pay-ops-usage__role-options code.pay-ops-usage__role-id--matched {
  border-color: #6ce9a6;
  color: #027a48;
  background: #fff;
}

/* Workflow notes ------------------------------------------------------- */
.pay-ops-usage__note-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.pay-ops-usage__note-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-height: 5rem;
  border: 1px solid #d0d5dd;
  border-left: 4px solid #98a2b3;
  border-radius: 0.6rem;
  background: #fff;
  padding: 0.8rem;
}

.pay-ops-usage__note-row--success {
  border-left-color: #039855;
  background: #f6fef9;
}
.pay-ops-usage__note-row--warning {
  border-left-color: #f79009;
  background: #fffcf5;
}
.pay-ops-usage__note-row--danger {
  border-left-color: #d92d20;
  background: #fffafa;
}
.pay-ops-usage__note-row--info {
  border-left-color: #0ba5ec;
  background: #f5fbff;
}
.pay-ops-usage__note-row--primary {
  border-left-color: #2e3482;
  background: #f8f9ff;
}
.pay-ops-usage__note-row--dark {
  border-left-color: #344054;
  background: #f9fafb;
}
.pay-ops-usage__note-row--neutral {
  border-left-color: #98a2b3;
  background: #f9fafb;
}

.pay-ops-usage__note-icon {
  display: inline-flex;
  width: 1.85rem;
  height: 1.85rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.pay-ops-usage__note-icon--success {
  background: #d1fadf;
  color: #027a48;
}
.pay-ops-usage__note-icon--warning {
  background: #fef0c7;
  color: #b54708;
}
.pay-ops-usage__note-icon--danger {
  background: #fee4e2;
  color: #b42318;
}
.pay-ops-usage__note-icon--info {
  background: #e0f2fe;
  color: #026aa2;
}
.pay-ops-usage__note-icon--primary {
  background: #e9eaff;
  color: #2e3482;
}
.pay-ops-usage__note-icon--dark {
  background: #eaecf0;
  color: #344054;
}
.pay-ops-usage__note-icon--neutral {
  background: #f2f4f7;
  color: #475467;
}

.pay-ops-usage__note-content {
  flex: 1 1 auto;
  min-width: 0;
  color: #475467;
  font-size: 0.84rem;
  line-height: 1.55;
}

.pay-ops-usage__note-title {
  display: block;
  margin-bottom: 0.2rem;
  color: #182230;
  font-size: 0.9rem;
  font-weight: 800;
}

.pay-ops-usage__note-body {
  text-transform: none;
}

.pay-ops-usage__note-body :deep(code) {
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.05);
  font-size: 0.85em;
}

.pay-ops-usage__note-body :deep(strong) {
  color: #182230;
}

@media (max-width: 575.98px) {
  .pay-ops-usage__access {
    display: none;
  }

  .pay-ops-usage__section-heading {
    flex-direction: column;
  }

  .pay-ops-usage__role-summary {
    width: 100%;
  }
}

@media (max-width: 991.98px) {
  .pay-ops-usage__access-levels,
  .pay-ops-usage__note-list {
    grid-template-columns: 1fr;
  }
}
</style>
